/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { FC, memo, useState, useEffect } from 'react';
import {
  Navbar,
  Nav,
  Button,
  Modal,
  Form,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation, useMatch } from 'react-router-dom';

import classnames from 'classnames';

import {
  userCenter,
  floppyNavigation,
  isLight,
  isDarkTheme,
  changeTheme,
  Storage,
} from '@/utils';
import {
  loggedUserInfoStore,
  siteInfoStore,
  brandingStore,
  themeSettingStore,
  sideNavStore,
} from '@/stores';
import {
  logout,
  useQueryNotificationStatus,
  createInvite,
  listMyInvites,
} from '@/services';
import type { Invite } from '@/common/interface';
import { Icon, MobileSideNav } from '@/components';
import {
  SHOW_COMMUNITY_SIDE_NAV,
  COLOR_SCHEME_STORAGE_KEY,
} from '@/common/constants';
import GeneralTestsNav from '../GeneralTestsNav';
import { getRegion, switchRegion } from '@/utils/region';
import ImportProgress from '../ImportProgress';

import SearchInput from './components/SearchInput';
import NavItems from './components/NavItems';

import './index.scss';

const Header: FC = () => {
  const location = useLocation();
  const { user, clear: clearUserStore } = loggedUserInfoStore();
  const { t } = useTranslation();
  const siteInfo = siteInfoStore((state) => state.siteInfo);
  const brandingInfo = brandingStore((state) => state.branding);
  const { updateReview } = sideNavStore();
  const { data: redDot } = useQueryNotificationStatus();
  const [showMobileSideNav, setShowMobileSideNav] = useState(false);

  const [showMobileSearchInput, setShowMobileSearchInput] = useState(false);
  const [nightMode, setNightMode] = useState(isDarkTheme());
  const otherRegion = getRegion() === 'br' ? 'intl' : 'br';

  // Invite-a-friend modal
  const [showInvite, setShowInvite] = useState(false);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');

  const openInvite = () => {
    setShowInvite(true);
    setInviteEmail('');
    setInviteError('');
    setInvitesLoading(true);
    listMyInvites()
      .then((data) => setInvites(data || []))
      .catch((e: any) =>
        setInviteError(e.message || t('site_header.err_load_invites')),
      )
      .finally(() => setInvitesLoading(false));
  };

  const handleCreateInvite = () => {
    setInviting(true);
    setInviteError('');
    createInvite(inviteEmail.trim() || undefined)
      .then((invite) => {
        setInvites((prev) => [invite, ...prev]);
        setInviteEmail('');
      })
      .catch((e: any) =>
        setInviteError(e.message || t('site_header.err_create_invite')),
      )
      .finally(() => setInviting(false));
  };

  // Explicit choice, remembered — it stops following the OS from here on.
  const toggleNightMode = () => {
    const next = isDarkTheme() ? 'light' : 'dark';
    Storage.set(COLOR_SCHEME_STORAGE_KEY, next);
    changeTheme(next);
    setNightMode(next === 'dark');
  };

  /**
   * Automatically append `tag` information when creating a question
   */
  const tagMatch = useMatch('/tags/:slugName');
  let askUrl = '/questions/add';
  if (tagMatch && tagMatch.params.slugName) {
    askUrl = `${askUrl}?tags=${encodeURIComponent(tagMatch.params.slugName)}`;
  }

  useEffect(() => {
    updateReview({
      can_revision: Boolean(redDot?.can_revision),
      revision: Number(redDot?.revision),
    });
  }, [redDot]);

  const handleLogout = async (evt) => {
    evt.preventDefault();
    await logout();
    clearUserStore();
    window.location.replace(window.location.href);
  };

  useEffect(() => {
    setShowMobileSearchInput(false);
    setShowMobileSideNav(false);
  }, [location.pathname]);

  let navbarStyle = 'theme-light';
  let themeMode = 'light';
  const { theme, theme_config, layout } = themeSettingStore((_) => _);
  if (theme_config?.[theme]?.navbar_style) {
    // const color = theme_config[theme].navbar_style.startsWith('#')
    themeMode = isLight(theme_config[theme].navbar_style) ? 'light' : 'dark';
    navbarStyle = `theme-${themeMode}`;
  }

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1199.9) {
        setShowMobileSideNav(false);
        setShowMobileSearchInput(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <Navbar
        data-bs-theme={themeMode}
        expand="xl"
        className={classnames('sticky-top', navbarStyle)}
        style={{
          backgroundColor: theme_config[theme].navbar_style,
        }}
        id="header">
        <div
          className={classnames(
            'w-100 d-flex align-items-center',
            layout === 'Fixed-width' ? 'container-xxl fixed-width' : 'px-3',
          )}>
          {(SHOW_COMMUNITY_SIDE_NAV ||
            location.pathname.includes('/admin')) && (
            <Navbar.Toggle
              className="answer-navBar me-2"
              onClick={() => {
                setShowMobileSideNav(!showMobileSideNav);
                setShowMobileSearchInput(false);
              }}
            />
          )}

          <Navbar.Brand
            to="/"
            as={Link}
            className="lh-1 me-0 me-sm-5 p-0 nav-text">
            {brandingInfo.logo ? (
              <>
                <img
                  className="d-none d-xl-block logo me-0"
                  src={brandingInfo.logo}
                  alt={siteInfo.name}
                />

                <img
                  className="xl-none logo me-0"
                  src={brandingInfo.mobile_logo || brandingInfo.logo}
                  alt={siteInfo.name}
                />
              </>
            ) : (
              <span>{siteInfo.name}</span>
            )}
          </Navbar.Brand>

          {/* Categories menu sits left of the search, next to the logo */}
          <Nav className="d-none d-xl-flex me-4">
            <GeneralTestsNav />
          </Nav>

          {/* Imports in progress sit left of the search box, which stays
              short ("Search" + icon) until it is clicked. */}
          <div className="d-none d-lg-flex align-items-center ms-auto me-3">
            <ImportProgress />
            <SearchInput className="header-search" />
          </div>

          <Nav className="d-block d-lg-none me-2 ms-auto">
            <Button
              variant="link"
              onClick={() => {
                setShowMobileSideNav(false);
                setShowMobileSearchInput(!showMobileSearchInput);
              }}
              className="p-0 btn-no-border icon-link nav-link d-flex align-items-center justify-content-center">
              <Icon name="search" className="lh-1 fs-4" />
            </Button>
          </Nav>

          {/* The other site, named in its own language: Brazilians read
            "Brasil" on passaly.com, everyone else "International" on .com.br.
            A visible link rather than a redirect, so search engines and
            visitors abroad still reach both. */}
          <Nav className="me-1 me-sm-2">
            <Button
              variant="link"
              title={
                otherRegion === 'br'
                  ? 'Passaly Brasil'
                  : 'Passaly International'
              }
              onClick={() => switchRegion(otherRegion)}
              className="p-0 btn-no-border nav-link d-flex align-items-center text-nowrap">
              <Icon name="globe2" className="lh-1 fs-4" />
              <span className="ms-1 d-none d-sm-inline small">
                {otherRegion === 'br' ? 'Brasil' : 'International'}
              </span>
            </Button>
          </Nav>

          {/* night mode — outside both branches so it shows logged out too.
            Tighter margin on phones: this row is already full at 360px. */}
          <Nav className="me-1 me-sm-2">
            <Button
              variant="link"
              title={
                nightMode
                  ? t('site_header.day_mode')
                  : t('site_header.night_mode')
              }
              onClick={toggleNightMode}
              className="p-0 btn-no-border icon-link nav-link d-flex align-items-center justify-content-center">
              <Icon
                name={nightMode ? 'sun' : 'moon-stars'}
                className="lh-1 fs-4"
              />
            </Button>
          </Nav>

          {/* pc nav */}
          {user?.username ? (
            <Nav className="d-flex align-items-center flex-nowrap flex-row">
              <Nav.Item className="me-2 d-block d-xl-none">
                <NavLink
                  to={askUrl}
                  className="d-block icon-link nav-link text-center">
                  <Icon name="plus-lg" className="lh-1 fs-4" />
                </NavLink>
              </Nav.Item>

              {/* tests entry for phones — the dropdown below is xl-only */}
              <Nav.Item className="me-2 d-block d-xl-none">
                <NavLink
                  to="/public-tests"
                  title={t('site_header.tests')}
                  className="d-block icon-link nav-link text-center">
                  <Icon name="card-checklist" className="lh-1 fs-4" />
                </NavLink>
              </Nav.Item>

              <Nav.Item className="me-2 d-none d-xl-block">
                <NavLink
                  to={askUrl}
                  className="nav-link d-flex align-items-center text-capitalize text-nowrap">
                  <Icon name="plus-lg" className="me-2 lh-1 fs-4" />
                  <span>{t('btns.create')}</span>
                </NavLink>
              </Nav.Item>

              <Nav.Item className="me-2 d-none d-xl-block">
                <NavLink to="/tests" className="nav-link text-nowrap">
                  {t('site_header.my_tests')}
                </NavLink>
              </Nav.Item>

              <Nav.Item className="me-2 d-none d-xl-block">
                {/* /blog is server-rendered outside the SPA router - a plain
                  anchor, not NavLink, so it does a real page load. */}
                <a href="/blog" className="nav-link text-nowrap">
                  {t('site_header.blog')}
                </a>
              </Nav.Item>

              <Nav.Item className="me-2 d-none d-xl-block">
                <Button
                  variant="link"
                  className="nav-link text-nowrap"
                  onClick={openInvite}>
                  {t('site_header.invite')}
                </Button>
              </Nav.Item>

              <NavItems redDot={redDot} userInfo={user} logOut={handleLogout} />
            </Nav>
          ) : (
            <>
              {/* tests entry for phones — the dropdown below is xl-only */}
              <Nav className="me-2 d-flex d-xl-none">
                <NavLink
                  to="/public-tests"
                  title={t('site_header.tests')}
                  className="icon-link nav-link d-flex align-items-center">
                  <Icon name="card-checklist" className="lh-1 fs-4" />
                </NavLink>
              </Nav>
              <Link
                className="me-2 btn btn-primary text-white d-none d-xl-inline-block"
                to="/public-tests">
                {t('site_header.practice_tests')}
              </Link>
              <a
                href="/blog"
                className={classnames(
                  'me-2 btn btn-link d-none d-xl-inline-block',
                  {
                    'link-light': navbarStyle === 'theme-dark',
                    'link-primary': navbarStyle !== 'theme-dark',
                  },
                )}>
                {t('site_header.blog')}
              </a>
              <Link
                className={classnames(
                  'icon-link nav-link d-flex align-items-center',
                  navbarStyle === 'theme-dark' ? 'link-light' : 'link-primary',
                )}
                title={t('btns.login')}
                aria-label={t('btns.login')}
                onClick={() => floppyNavigation.storageLoginRedirect()}
                to={userCenter.getLoginUrl()}>
                <Icon name="person-circle" className="lh-1 fs-3" />
              </Link>
            </>
          )}
        </div>

        {showMobileSearchInput && (
          <div className="w-100 px-3 mt-2 d-block d-lg-none">
            <SearchInput />
          </div>
        )}

        <MobileSideNav show={showMobileSideNav} onHide={setShowMobileSideNav} />
      </Navbar>

      <Modal show={showInvite} onHide={() => setShowInvite(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('site_header.invite_friend')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {inviteError && <Alert variant="danger">{inviteError}</Alert>}
          <p className="text-muted small">{t('site_header.invite_help')}</p>
          <Form.Group className="mb-3 d-flex gap-2">
            <Form.Control
              type="email"
              placeholder={t('site_header.friend_email')}
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreateInvite();
                }
              }}
              disabled={inviting}
            />
            <Button
              variant="primary"
              onClick={handleCreateInvite}
              disabled={inviting}>
              {inviting ? (
                <Spinner animation="border" size="sm" />
              ) : (
                t('site_header.create')
              )}
            </Button>
          </Form.Group>
          {invitesLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
            </div>
          ) : invites.length === 0 ? (
            <p className="text-muted small mb-0">
              {t('site_header.no_invites')}
            </p>
          ) : (
            <ul className="list-unstyled mb-0">
              {invites.map((inv) => (
                <li key={inv.code} className="py-2 border-bottom">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-truncate" style={{ maxWidth: '70%' }}>
                      {inv.email || inv.link}
                    </span>
                    <span
                      className={classnames(
                        'badge',
                        inv.status === 2 ? 'bg-secondary' : 'bg-success',
                      )}>
                      {inv.status === 2
                        ? t('site_header.used')
                        : t('site_header.pending')}
                    </span>
                  </div>
                  {inv.status !== 2 && (
                    <div className="text-muted small text-truncate">
                      {inv.link}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInvite(false)}>
            {t('site_header.done')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default memo(Header);
